import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import Webcam from 'react-webcam'
import styled from 'styled-components'
import Lottie from 'lottie-react'
import {
  blobToFile,
  handleUploadSingleFileToWasabi,
  showSnackbar,
  urlToBlob,
  useFirestoreEditCredentialKycStatus,
  useHistory,
} from 'utils'
import {
  AuthCredentialType,
  GiftshopModalStates,
  ObjectState,
  ProfileEditKycStatus,
  ProfileEditScreenType,
  ProfileEditSelfiePage,
} from 'types'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {useWindowMode} from 'windows'
import {Modal, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {useDispatch, useSelector} from 'lib/redux'
import animationData from 'assets/lottie/activity_indicator.json'
import {
  GiftShopTemplatePermissionDenied,
  GiftShopTemplateUploadModal,
} from '../../../template'
import {GiftShopEditProfileModalKYCStatus} from '../Modal'
import GiftShopEditProfileSelfieOverlay from './GiftShopEditProfileSelfieOverlay'
import GiftShopEditProfileSelfieConfirmation from './GiftShopEditProfileSelfieConfirmation'
import GiftShopEditProfileSelfieLanding from './GiftShopEditProfileSelfieLanding'

interface ContainerProps {
  screen: ProfileEditSelfiePage
}

const StyledContainer = styled.div<ContainerProps>`
  display: flex;
  height: 100%;
  justify-content: center;
  background-color: ${({screen, theme}) =>
    screen === 'shoot' ? theme.black : undefined};
`
const StyledContentContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  max-width: ${convertUnit(600)};
`
const StyledLottie = styled(Lottie)`
  height: ${convertUnit(40)};
  width: ${convertUnit(40)};
  margin-bottom: ${convertUnit(25)};
`
const StyledModal = styled(Modal)`
  width: 100%;
  max-width: ${convertUnit(335)};
  padding: ${convertUnit(20)};
  display: flex;
  text-align: center;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`
const StyledParagraph = styled(Paragraph)`
  margin-top: ${convertUnit(4)};
`

interface GiftShopEditProfileSelfieProps {
  method: AuthCredentialType
  stateScreen: ObjectState<ProfileEditSelfiePage>
  statePassword: ObjectState<string>
  stateEditProfileScreen?: ObjectState<ProfileEditScreenType>
  stateModal?: ObjectState<GiftshopModalStates>
}

export default function GiftShopEditProfileSelfie({
  method,
  stateScreen,
  statePassword,
  stateEditProfileScreen,
  stateModal,
}: GiftShopEditProfileSelfieProps) {
  const {translate} = useTranslation()
  const {email: newEmail} = useSelector('userNewEmail') || {}
  const {phoneCode, phoneNumber} = useSelector('userNewPhone') || {}
  const history = useHistory()
  const mode = useWindowMode()
  const [screen, setScreen] = stateScreen
  const webcamRef = useRef<any>(null)
  const videoConstraint = useMemo(
    () => ({
      aspectRatio: mode === 'mobile' ? 1.5 : 0.67,
      facingMode: 'user',
    }),
    [mode],
  )
  const [selfie, setSelfie] = useState('')
  const [mediaError, setMediaError] = useState(false)
  const [verifStatus, setVerifStatus] = useState<
    ProfileEditKycStatus | undefined
  >()
  const [isFailed, setIsFailed] = useState(false)
  const {update} = useDispatch()
  const uploadSuccess = useRef(0)
  const {
    verification_email_status,
    verification_phone_status,
  } = useFirestoreEditCredentialKycStatus()
  const [uploadProgress, setUploadProgress] = useState<
    'preparing' | 'uploading'
  >('preparing')
  const [modalUploading, setModalUploading] = useState(false)
  const [loadingInModal, setLoadingInModal] = useState(false)
  const isUploadFinished = useRef(false)
  const isFirstUpload = useRef(true)

  const handleTakeSelfie = useCallback(() => {
    if (webcamRef.current) {
      const image = webcamRef.current.getScreenshot()
      setSelfie(image)
      setScreen('confirmation')
    }
  }, [setScreen])

  const handleRetake = useCallback(() => {
    setIsFailed(false)
    setScreen('shoot')
    setSelfie('')
  }, [setScreen])

  const handle2FAEditCredentialSendNew = useCallback(() => {
    if (method === 'email') {
      setLoadingInModal(true)
      requestData('member_2fa_edit_email_send_new', {
        onRequestReceived: () => setLoadingInModal(false),
        onRequestSuccess: ({status}) => {
          if (status === 200) {
            update('lastUserState', {emailTime: new Date().getTime()})
            setVerifStatus('verified')
          } else {
            setVerifStatus(undefined)
            showSnackbar(
              translate('global:messageError', {context: `code-${status}`}),
            )
          }
        },
      })
    } else {
      setLoadingInModal(true)
      requestData('member_2fa_edit_phone_send_new', {
        data: {
          verification_method: 'whatsapp',
        },
        onRequestReceived: () => setLoadingInModal(false),
        onRequestSuccess: ({status}) => {
          if (status === 200) {
            update('lastUserState', {phoneOTPTime: new Date().getTime()})
            update('editProfileState', {showModalPhoneOtp: true})
            setVerifStatus('verified')
          } else {
            setVerifStatus(undefined)
            showSnackbar(
              translate('global:messageError', {context: `code-${status}`}),
            )
          }
        },
      })
    }
  }, [method, translate, update])

  const handleFinishKycProcess = useCallback(
    (status: ProfileEditKycStatus) => {
      isUploadFinished.current = false
      if (status === 'verified') {
        update(
          'editProfileState',
          method === 'email'
            ? {
                isOldEmailConfirmed: true,
                verifMethodEmail: undefined,
              }
            : {
                isOldPhoneConfirmed: true,
                verifMethodPhone: undefined,
              },
        )
        handle2FAEditCredentialSendNew()
      } else {
        setVerifStatus(status)
      }
    },
    [handle2FAEditCredentialSendNew, method, update],
  )

  const handleUploadLink = useCallback(
    async (link: string, file: File, extension: string) => {
      const isSuccess = await handleUploadSingleFileToWasabi(
        file,
        link,
        extension,
        {
          'Content-Type': extension,
        },
      )
      if (isSuccess) {
        uploadSuccess.current += 1
        const response = await requestData('member_2fa_kyc', {
          data: {selfie_link: link, type: `edit_${method}`},
        })
        if (typeof response !== 'string') {
          if (response.status === 200) {
            isUploadFinished.current = true
            setModalUploading(false)
            setVerifStatus('waiting')
          } else {
            setModalUploading(false)
            showSnackbar(
              translate('global:messageError', {
                context: `code-${response.status}`,
              }),
            )
          }
        } else {
          setModalUploading(false)
          showSnackbar(translate('global:messageError'))
        }
      } else {
        setModalUploading(false)
        showSnackbar(translate('global:messageError'))
      }
    },
    [method, translate],
  )

  const handleUploadFirst = useCallback(
    async (size: number, extension: string, file: File) => {
      if (method === 'email' && newEmail) {
        const response = await requestData(
          'member_2fa_edit_email_send_current',
          {
            data: {
              email: newEmail,
              password: statePassword[0],
              size,
              extension: extension.split('/')[1],
              verification_method: 'kyc',
            },
          },
        )
        if (typeof response !== 'string') {
          if (response.status === 200) {
            statePassword[1]('')
            isFirstUpload.current = false
            const {selfie_link} = response.data.result
            await handleUploadLink(selfie_link, file, extension)
          } else {
            setModalUploading(false)
            showSnackbar(
              translate('global:messageError', {
                context: `code-${response.status}`,
              }),
            )
          }
        } else {
          setModalUploading(false)
          showSnackbar(translate('global:messageError'))
        }
      } else if (phoneCode && phoneNumber) {
        const response = await requestData(
          'member_2fa_edit_phone_send_current',
          {
            data: {
              phone_code: phoneCode,
              phone_number: phoneNumber,
              password: statePassword[0],
              size,
              extension: extension.split('/')[1],
              verification_method: 'kyc',
            },
          },
        )
        if (typeof response !== 'string') {
          if (response.status === 200) {
            statePassword[1]('')
            isFirstUpload.current = false
            const {selfie_link} = response.data.result
            await handleUploadLink(selfie_link, file, extension)
          } else {
            setModalUploading(false)
            showSnackbar(
              translate('global:messageError', {
                context: `code-${response.status}`,
              }),
            )
          }
        } else {
          setModalUploading(false)
          showSnackbar(translate('global:messageError'))
        }
      }
    },
    [
      handleUploadLink,
      method,
      newEmail,
      phoneCode,
      phoneNumber,
      statePassword,
      translate,
    ],
  )

  const handleUploadResend = useCallback(
    async (size: number, extension: string, file: File) => {
      const response = await requestData('member_2fa_kyc_link', {
        data: {
          size,
          extension: extension.split('/')[1],
          type: `edit_${method}`,
        },
      })
      if (typeof response !== 'string') {
        if (response.status === 200) {
          setUploadProgress('uploading')
          const {selfie_link} = response.data.result
          handleUploadLink(selfie_link, file, extension)
        } else {
          setModalUploading(false)
          showSnackbar(
            translate('global:messageError', {
              context: `code-${response.status}`,
            }),
          )
        }
      } else {
        setModalUploading(false)
        showSnackbar(translate('global:messageError'))
      }
    },
    [handleUploadLink, method, translate],
  )

  const handleUpload = useCallback(async () => {
    setUploadProgress('preparing')
    uploadSuccess.current = 0
    setModalUploading(true)
    const selfieBlob = await urlToBlob(selfie)
    const fileSelfie = blobToFile(
      selfieBlob,
      'kyc-selfie',
      new Date().getTime(),
    )
    const selfieExt = fileSelfie.type
    if (isFirstUpload.current) {
      handleUploadFirst(fileSelfie.size, selfieExt, fileSelfie)
    } else {
      handleUploadResend(fileSelfie.size, selfieExt, fileSelfie)
    }
  }, [handleUploadFirst, handleUploadResend, selfie])

  const handleRenderModalStatus = useMemo(
    () => (
      <GiftShopEditProfileModalKYCStatus
        visible={verifStatus !== undefined}
        method={method}
        loading={loadingInModal}
        status={verifStatus}
        onSecondaryAction={() => {
          isUploadFinished.current = false
          setVerifStatus(undefined)
          setIsFailed(true)
        }}
        onPrimaryAction={() => {
          if (verifStatus === 'verified') {
            setVerifStatus(undefined)
            if (method === 'email') {
              history.replace('auth_verification_email', {
                email: newEmail,
                disableCensor: true,
              })
            } else {
              stateEditProfileScreen && stateEditProfileScreen[1]('form')
              stateModal && stateModal[1]('verify')
            }
          } else {
            isUploadFinished.current = false
            setVerifStatus(undefined)
            handleRetake()
          }
        }}
      />
    ),
    [
      verifStatus,
      method,
      loadingInModal,
      history,
      newEmail,
      stateEditProfileScreen,
      stateModal,
      handleRetake,
    ],
  )

  const handleRenderNeedPermission = useMemo(
    () => <GiftShopTemplatePermissionDenied type="camera" />,
    [],
  )

  const handleRenderOverlay = useMemo(
    () => <GiftShopEditProfileSelfieOverlay onClick={handleTakeSelfie} />,
    [handleTakeSelfie],
  )

  const handleRenderWebcam = useMemo(
    () => (
      <>
        <Webcam
          style={{height: '100%', position: 'absolute'}}
          mirrored
          onUserMediaError={() => setMediaError(true)}
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={videoConstraint}
        />
        {handleRenderOverlay}
      </>
    ),
    [handleRenderOverlay, videoConstraint],
  )

  const renderModalUploading = useMemo(
    () => (
      <StyledModal visible={modalUploading}>
        {uploadProgress === 'preparing' ? (
          <>
            <StyledLottie animationData={animationData} />
            <Paragraph fontSize="xl" fontWeight="bold">
              {translate('giftShop:kycUploading_preparing')}
            </Paragraph>
          </>
        ) : (
          <GiftShopTemplateUploadModal
            title={translate('giftShop:uploadPublishLoadingTitle')}
            description={translate('giftShop:uploadPublishLoadingDescription', {
              success: uploadSuccess.current,
              total: 1,
            })}
            progressColor="primary_5"
            success={uploadSuccess.current}
            total={1}
          />
        )}
        <StyledParagraph fontSize="m" color="gray_3">
          {translate('auth:registerEasySignupUploadWarning')}
        </StyledParagraph>
      </StyledModal>
    ),
    [modalUploading, translate, uploadProgress],
  )

  const handleRenderScreen = useMemo(() => {
    switch (screen) {
      case 'confirmation':
        return (
          selfie && (
            <StyledContentContainer>
              <GiftShopEditProfileSelfieConfirmation
                onAccept={handleUpload}
                onRetry={handleRetake}
                selfie={selfie}
                isFailed={isFailed}
              />
            </StyledContentContainer>
          )
        )
      case 'shoot':
        return mediaError ? handleRenderNeedPermission : handleRenderWebcam
      default:
        return (
          <StyledContentContainer>
            <GiftShopEditProfileSelfieLanding
              onClick={() => setScreen('shoot')}
            />
          </StyledContentContainer>
        )
    }
  }, [
    handleRenderNeedPermission,
    handleRenderWebcam,
    handleRetake,
    handleUpload,
    isFailed,
    mediaError,
    screen,
    selfie,
    setScreen,
  ])

  useEffect(() => {
    if (
      method === 'email' &&
      isUploadFinished.current &&
      verification_email_status &&
      verification_email_status !== 'waiting'
    ) {
      handleFinishKycProcess(verification_email_status)
    }
  }, [handleFinishKycProcess, method, verification_email_status])

  useEffect(() => {
    if (
      method === 'phone' &&
      isUploadFinished.current &&
      verification_phone_status &&
      verification_phone_status !== 'waiting'
    ) {
      handleFinishKycProcess(verification_phone_status)
    }
  }, [handleFinishKycProcess, method, verification_phone_status])

  return (
    <StyledContainer screen={screen}>
      {handleRenderModalStatus}
      {renderModalUploading}
      {handleRenderScreen}
    </StyledContainer>
  )
}
